import { ofType } from 'redux-observable';
import type { Observable } from 'rxjs';
import { combineLatest, EMPTY, merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { unhandledError } from 'state/actions';
import type { Dispatchable, EpicWithThunk } from 'state/state.common';
import { workspaceTabQuoteRepriced } from 'state/workspaces/workspace.actions';
import { getWorkspaceTabIds } from 'state/workspaces/workspaces/workspaces.selector';

export function dynamicWorkspaceRepriceEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getCurrentRoute }, getNewGuid, navigateTo }) => {
    return action$.pipe(
      ofType('WORKSPACE_TAB_REPRICE_REQUESTED'),
      filter(({ workspace }) => workspace === 'dynamic'),
      withLatestFrom(state$),
      mergeMap(([{ id, idType, rfqId, pricerRfqId, externalQuoteId, withLastQuote, processingChain }, state]) => {
        const currentRoute = getCurrentRoute(state);
        const isWorkspaceInitialized: boolean = !!getWorkspaceTabIds(state, 'dynamic')?.length;

        const dynamicTabsInitialized$: Observable<boolean> = isWorkspaceInitialized
          ? of(true)
          : action$.pipe(
              ofType('WORKSPACE_PERSISTED_TABS_APPLIED', 'WORKSPACE_TAB_CREATED'),
              filter(({ workspace }) => workspace === 'dynamic'),
              map(() => true),
              take(1),
            );

        const navigateToDynamicScreenSucceed$ =
          currentRoute === '/dynamic'
            ? of(true)
            : action$.pipe(
                ofType('ROUTE_CHANGED'),
                filter(({ path }) => path === '/dynamic'),
                take(1),
              );

        const navigateToDynamic$: Observable<Dispatchable> = of(true).pipe(
          tap(() => {
            if (currentRoute !== '/dynamic') {
              navigateTo('/dynamic');
            }
          }),
          mergeMap(() => EMPTY),
        );

        return merge(
          combineLatest([dynamicTabsInitialized$, navigateToDynamicScreenSucceed$]).pipe(
            map(() =>
              workspaceTabQuoteRepriced(
                getNewGuid(),
                id,
                idType,
                rfqId,
                pricerRfqId,
                'dynamic',
                withLastQuote,
                processingChain,
                externalQuoteId,
              ),
            ),
            catchError(err =>
              of(unhandledError('staticWorkspaceRepriceEpic failed', err, 'dynamicWorkspaceRepriceEpic')),
            ),
          ),
          navigateToDynamic$,
        );
      }),
    );
  };
}
